<script setup lang="ts">
import { ref, onMounted, reactive, computed, watch } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Badge from '@/components/ui/Badge.vue';
import Textarea from '@/components/ui/TextArea.vue';
import Button from '@/components/ui/UIButton.vue';
import UISelect from '@/components/ui/UISelect.vue';
import { Brain, TestTube, ChevronDown, ChevronUp, Undo2 } from 'lucide-vue-next';
import { aiService } from '@/services/AIService';
import type { Prompt } from '@/types/ui/PromptManager';

// ------
// emits
// ------

const emit = defineEmits<{
  promptSelect: [prompt: Prompt];
  contentChange: [content: string];
  promptReset: [prompt: Prompt];
  systemInstructionsChange: [content: string];
  testPromptChange: [systemText: string, promptText: string];
}>();

// -----
// props
// -----

interface Props {
  selectedPrompt: Prompt | null;
  currentContent: string;
  hasUnsavedChanges: boolean;
  expectedOutputSchema?: string;
}

const props = defineProps<Props>();

// -----
// data
// -----

const prompts = ref<Prompt[]>([]);
const loading = ref(false);
const isSystemInstructionsExpanded = ref(false);
const isPromptTextExpanded = ref(true);
const isExpectedOutputExpanded = ref(false);
const promptMode = ref<'available' | 'test'>('available');
const testPrompt = reactive({
  systemInstructions: '',
  promptText: '',
});

// Dropdown selections
const selectionState = reactive({
  selectedPromptName: '',
  selectedVersion: '',
  availableVersions: [] as Prompt[],
});

// ---------
// computed
// ---------

// Group prompts by ID for dropdown display, using draft name as the display name
const groupedPrompts = computed(() => {
  const groups = new Map<string, Prompt[]>();

  // Filter out temporary new prompts
  const filteredPrompts = prompts.value.filter((prompt) => !startsWith(prompt.promptId, 'new-'));

  filteredPrompts.forEach((prompt) => {
    const promptId = String(prompt.promptId) || 'unknown';
    if (!groups.has(promptId)) {
      groups.set(promptId, []);
    }
    groups.get(promptId)!.push(prompt);
  });

  // Sort each group's versions (draft first, then by version number descending)
  groups.forEach((versions) => {
    versions.sort((a, b) => {
      const aIsDraft = a.version.toLowerCase() === 'draft';
      const bIsDraft = b.version.toLowerCase() === 'draft';

      if (aIsDraft && !bIsDraft) return -1;
      if (!aIsDraft && bIsDraft) return 1;

      if (aIsDraft && bIsDraft) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }

      const aVersion = parseInt(a.version) || 0;
      const bVersion = parseInt(b.version) || 0;
      return bVersion - aVersion;
    });
  });

  // Create a map with display names (from draft) as keys and prompt groups as values
  const displayGroups = new Map<string, Prompt[]>();

  groups.forEach((versions) => {
    // Find the draft version to get the canonical name
    const draftVersion = versions.find((p) => p.version.toLowerCase() === 'draft');
    const displayName = draftVersion?.name || versions[0]?.name || 'Unnamed';

    displayGroups.set(displayName, versions);
  });

  // Sort by display name alphabetically
  return new Map([...displayGroups.entries()].sort(([a], [b]) => a.localeCompare(b)));
});

// Computed properties for UISelect options
const promptOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select a prompt...' }];

  for (const [promptName] of groupedPrompts.value) {
    options.push({
      value: promptName,
      label: promptName,
    });
  }

  return options;
});

const versionOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [];

  for (const version of selectionState.availableVersions) {
    let label = formatVersion(version.version);
    if (version.isActive) {
      label += ' (Active)';
    }

    options.push({
      value: version.version,
      label: label,
    });
  }

  return options;
});

const isReadonly = computed(() => {
  return promptMode.value === 'available';
});

const currentSystemText = computed({
  get: () => {
    if (promptMode.value === 'test') {
      return testPrompt.systemInstructions;
    }
    return props.selectedPrompt?.currentSystemText || props.selectedPrompt?.systemPrompt || '';
  },
  set: (value: string) => {
    if (promptMode.value === 'test') {
      testPrompt.systemInstructions = value;
      emit('testPromptChange', value, testPrompt.promptText);
    } else {
      emit('systemInstructionsChange', value);
    }
  },
});

const currentPromptText = computed({
  get: () => {
    if (promptMode.value === 'test') {
      return testPrompt.promptText;
    }
    return props.currentContent;
  },
  set: (value: string) => {
    if (promptMode.value === 'test') {
      testPrompt.promptText = value;
      emit('testPromptChange', testPrompt.systemInstructions, value);
    } else {
      emit('contentChange', value);
    }
  },
});

// --------
// lifecycle
// ---------

onMounted(async () => {
  await loadPrompts();
});

// Watch for prompt mode changes
watch(promptMode, (newMode) => {
  if (newMode === 'test') {
    // Initialize test prompt if empty
    if (!testPrompt.systemInstructions && !testPrompt.promptText) {
      testPrompt.systemInstructions = '';
      testPrompt.promptText = '';
    }
    emit('testPromptChange', testPrompt.systemInstructions, testPrompt.promptText);
  }
});

// Watch for prompt name selection
watch(
  () => selectionState.selectedPromptName,
  (newName) => {
    if (newName && groupedPrompts.value.has(newName)) {
      selectionState.availableVersions = groupedPrompts.value.get(newName)!;
      // Auto-select the first version (should be draft if available)
      if (selectionState.availableVersions.length > 0) {
        selectionState.selectedVersion = selectionState.availableVersions[0].version;
      }
    } else {
      selectionState.availableVersions = [];
      selectionState.selectedVersion = '';
    }
  },
);

// Watch for BOTH prompt name AND version selection
watch([() => selectionState.selectedPromptName, () => selectionState.selectedVersion], ([newName, newVersion]) => {
  if (newName && newVersion) {
    const prompt = selectionState.availableVersions.find((p) => p.version === newVersion);
    if (prompt) {
      emit('promptSelect', prompt);
    }
  }
});

// Watch for external selectedPrompt changes to sync dropdown state
watch(
  () => props.selectedPrompt,
  (newPrompt) => {
    if (newPrompt) {
      // Find all prompts with the same ID to get the draft name
      const sameIdPrompts = prompts.value.filter((p) => p.promptId === newPrompt.promptId);
      const draftVersion = sameIdPrompts.find((p) => p.version.toLowerCase() === 'draft');
      const displayName = draftVersion?.name || newPrompt.name || '';

      if (selectionState.selectedPromptName !== displayName) {
        selectionState.selectedPromptName = displayName;
      }
      if (selectionState.selectedVersion !== newPrompt.version) {
        selectionState.selectedVersion = newPrompt.version;
      }
    } else {
      // Clear selections when prompt is deselected
      selectionState.selectedPromptName = '';
      selectionState.selectedVersion = '';
      selectionState.availableVersions = [];
    }
  },
);

// ----------
// functions
// ----------

function startsWith(str: any, prefix: string) {
  return typeof str === "string" && str.startsWith(prefix);
};

const loadPrompts = async () => {
  loading.value = true;
  try {
    prompts.value = await aiService.getAllPrompts();
  } catch (e) {
    console.error('Failed to load prompts:', e);
  } finally {
    loading.value = false;
  }
};

const handlePromptReset = () => {
  if (props.selectedPrompt) {
    emit('promptReset', props.selectedPrompt);
  }
};

const clearTestPrompt = () => {
  testPrompt.systemInstructions = '';
  testPrompt.promptText = '';
  emit('testPromptChange', '', '');
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function formatVersion(version: string) {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  } else {
    return `v${version}`;
  }
}

const formatJsonSchema = (schema: string): string => {
  try {
    const parsed = JSON.parse(schema);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // If it's not valid JSON, return as-is
    return schema;
  }
};
</script>

<template>
  <Card class="tw:h-fit tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
    <CardHeader>
      <CardTitle class="tw:flex tw:items-center tw:justify-between">
        <div class="tw:flex tw:items-center tw:gap-2">
          <Brain class="tw:h-5 tw:w-5 tw:text-purple-600" />
          Prompts
        </div>
      </CardTitle>
      <CardDescription>Select from available prompts or create a test prompt</CardDescription>
    </CardHeader>
    <CardContent class="tw:flex tw:flex-col tw:gap-4 tw:space-y-4">
      <!-- Prompt Mode Selection -->
      <div class="tw:space-y-3">
        <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Prompt Type</h4>
        <div class="tw:flex tw:gap-2">
          <Button
            :variant="promptMode === 'available' ? 'default' : 'outline'"
            size="sm"
            @click="promptMode = 'available'"
            class="tw:gap-2">
            <Brain class="tw:h-4 tw:w-4" />
            Available Prompts
          </Button>
          <Button
            :variant="promptMode === 'test' ? 'default' : 'outline'"
            size="sm"
            @click="promptMode = 'test'"
            class="tw:gap-2">
            <TestTube class="tw:h-4 tw:w-4" />
            Test Prompt
          </Button>
        </div>
      </div>

      <!-- Available Prompts Mode -->
      <div v-if="promptMode === 'available'" class="tw:space-y-3">
        <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Available Prompts</h4>
        
        <!-- Loading State -->
        <div v-if="loading" class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50 tw:p-8 tw:text-center">
          <div
            class="tw:mx-auto tw:mb-3 tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-slate-200 tw:border-t-blue-500"></div>
          <p class="tw:text-sm tw:font-medium tw:text-slate-600">Loading prompts...</p>
        </div>
        
        <!-- Empty State -->
        <div
          v-else-if="promptOptions.length <= 1"
          class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50 tw:p-8 tw:text-center">
          <Brain class="tw:mx-auto tw:mb-3 tw:h-8 tw:w-8 tw:text-slate-400" />
          <p class="tw:text-sm tw:font-medium tw:text-slate-600">No available prompts</p>
          <p class="tw:mt-1 tw:text-xs tw:text-slate-500">Available prompts will appear here once they are published</p>
        </div>
        
        <!-- Prompt and Version Dropdowns -->
        <div v-else class="tw:space-y-3">
          <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4">
            <!-- Prompt Dropdown -->
            <div class="tw:flex tw:min-w-[200px] tw:flex-1 tw:items-center tw:gap-2">
              <label class="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:text-slate-700">Prompt:</label>
              <div class="tw:relative tw:flex-1">
                <UISelect
                  v-model="selectionState.selectedPromptName"
                  :options="promptOptions"
                  :disabled="loading"
                  placeholder="Select a prompt..." />
              </div>
            </div>

            <!-- Version Dropdown -->
            <div
              v-if="selectionState.selectedPromptName"
              class="tw:flex tw:min-w-[120px] tw:items-center tw:gap-2">
              <label class="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:text-slate-700">Version:</label>
              <div class="tw:relative">
                <UISelect
                  :key="`version-select-${selectionState.selectedPromptName}-${selectionState.availableVersions.length}`"
                  v-model="selectionState.selectedVersion"
                  :options="versionOptions"
                  :disabled="loading"
                  placeholder="Select version..." />
              </div>
            </div>
          </div>

          <!-- Selected Prompt Metadata -->
          <div v-if="props.selectedPrompt" class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50 tw:p-3">
            <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
              <p class="tw:text-sm tw:font-medium tw:text-slate-900">{{ props.selectedPrompt.name }}</p>
              <div class="tw:flex tw:items-center tw:gap-2">
                <Badge variant="outline" class="tw:text-xs">{{ formatVersion(props.selectedPrompt.version) }}</Badge>
                <Badge v-if="props.selectedPrompt.isActive" class="tw:bg-green-600 tw:text-xs">Active</Badge>
              </div>
            </div>
            <p class="tw:mt-1 tw:text-xs tw:text-slate-500">Updated {{ formatDate(props.selectedPrompt.updatedAt) }}</p>
            <p v-if="props.selectedPrompt.description" class="tw:mt-1 tw:text-xs tw:text-slate-600">{{ props.selectedPrompt.description }}</p>
          </div>
        </div>
      </div>

      <!-- Test Prompt Mode -->
      <div v-if="promptMode === 'test'" class="tw:space-y-3">
        <div class="tw:flex tw:items-center tw:justify-between">
          <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Test Prompt</h4>
          <Button
            v-if="testPrompt.systemInstructions || testPrompt.promptText"
            size="sm"
            variant="outline"
            @click="clearTestPrompt">
            Clear
          </Button>
        </div>
        <p class="tw:text-xs tw:text-slate-500">Create an ad-hoc prompt for testing. This will not be saved.</p>
      </div>

      <!-- Prompt Editor (for both modes) -->
      <div v-if="promptMode === 'available' ? props.selectedPrompt : true" class="tw:space-y-3">
        <div class="tw:flex tw:items-center tw:justify-between">
          <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">
            {{ promptMode === 'available' ? 'View Prompt' : 'Edit Test Prompt' }}
          </h4>
          <div class="tw:flex tw:place-items-end tw:space-x-3">
            <Button
              v-if="props.hasUnsavedChanges && promptMode === 'available'"
              @click="handlePromptReset"
              class="tw:btn tw:text-gray-400 tw:transition-colors tw:btn-xs tw:hover:text-gray-600"
              title="Reset changes">
              <Undo2 class="tw:h-4 tw:w-4" />
            </Button>
          </div>
        </div>

        <!-- System Instructions Section -->
        <div class="tw:space-y-2">
          <div
            class="tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:rounded tw:bg-slate-50 tw:px-3 tw:py-2 tw:transition-colors"
            @click="isSystemInstructionsExpanded = !isSystemInstructionsExpanded">
            <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">System Instructions</h4>
            <ChevronDown v-if="!isSystemInstructionsExpanded" class="tw:h-4 tw:w-4 tw:text-slate-500" />
            <ChevronUp v-else class="tw:h-4 tw:w-4 tw:text-slate-500" />
          </div>

          <div v-if="isSystemInstructionsExpanded" class="tw:space-y-2">
            <Textarea
              v-model="currentSystemText"
              :readonly="isReadonly"
              :PermanentBorder="true"
              :placeholder="
                promptMode === 'test'
                  ? 'Enter system instructions for the AI (optional)...'
                  : 'System instructions (read-only)'
              "
              :class="`tw:min-h-32 tw:font-mono tw:text-sm ${isReadonly ? 'tw:cursor-not-allowed tw:bg-slate-50' : ''}`" />
            <p v-if="isReadonly" class="tw:text-xs tw:text-slate-500 tw:italic">
              Available prompts are read-only. Use test prompts for editing.
            </p>
          </div>
        </div>

        <!-- Prompt Text Section -->
        <div class="tw:space-y-2">
          <div
            class="tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:rounded tw:bg-slate-50 tw:px-3 tw:py-2 tw:transition-colors"
            @click="isPromptTextExpanded = !isPromptTextExpanded">
            <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Prompt Text</h4>
            <ChevronDown v-if="!isPromptTextExpanded" class="tw:h-4 tw:w-4 tw:text-slate-500" />
            <ChevronUp v-else class="tw:h-4 tw:w-4 tw:text-slate-500" />
          </div>
          <div v-if="isPromptTextExpanded" class="tw:space-y-2">
            <Textarea
              v-model="currentPromptText"
              :readonly="isReadonly"
              :PermanentBorder="true"
              :placeholder="promptMode === 'test' ? 'Enter your test prompt...' : 'Prompt text (read-only)'"
              :class="`tw:min-h-64 tw:font-mono tw:text-sm ${isReadonly ? 'tw:cursor-not-allowed tw:bg-slate-50' : ''}`" />
            <p v-if="isReadonly" class="tw:text-xs tw:text-slate-500 tw:italic">
              Available prompts are read-only. Use test prompts for editing.
            </p>
          </div>
        </div>

        <!-- Expected Output Format Section -->
        <div v-if="props.expectedOutputSchema" class="tw:space-y-2">
          <div
            class="tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:rounded tw:bg-slate-50 tw:px-3 tw:py-2 tw:transition-colors"
            @click="isExpectedOutputExpanded = !isExpectedOutputExpanded">
            <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Expected Output Format</h4>
            <div class="tw:flex tw:items-center tw:gap-2">
              <ChevronDown v-if="!isExpectedOutputExpanded" class="tw:h-4 tw:w-4 tw:text-slate-500" />
              <ChevronUp v-else class="tw:h-4 tw:w-4 tw:text-slate-500" />
            </div>
          </div>

          <div v-if="isExpectedOutputExpanded" class="tw:space-y-2">
            <pre class="tw:overflow-x-auto tw:rounded tw:border tw:bg-slate-50 tw:p-3 tw:font-mono tw:text-xs">{{
              formatJsonSchema(props.expectedOutputSchema)
            }}</pre>
          </div>
        </div>

        <div v-if="props.hasUnsavedChanges && promptMode === 'available'" class="tw:flex tw:items-center tw:gap-2">
          <span class="tw:relative tw:flex tw:h-2 tw:w-2" title="Unsaved changes">
            <span
              class="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-pulse tw:rounded-full tw:bg-amber-400 tw:opacity-30"></span>
            <span class="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"></span>
          </span>
          <span class="tw:text-sm tw:text-amber-600">You have unsaved changes</span>
        </div>
      </div>
    </CardContent>
  </Card>
</template>
